import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AddItemRequest, AddPartRequest, CreateOrderRequest, OrderDetailResponse, PatchItemRequest, PatchOrderRequest, PatchPartRequest } from '@wf/shared'
import { api } from '../api/client'

export function useOrdersList() {
  return useQuery({ queryKey: ['orders'], queryFn: api.listOrders })
}

export function useOrder(id: number) {
  return useQuery({ queryKey: ['orders', id], queryFn: () => api.getOrder(id), enabled: Number.isFinite(id) })
}

// Lese-Hooks für die Übersicht (Keys identisch zu den bestehenden Invalidierungs-Zielen in usePatchOrder/
// useCreateInvoice/useDeleteOrder — sonst bräche die Auffrischung). `enabled` lädt nur, wenn die Rolle es
// serverseitig darf bzw. die Daten gebraucht werden (z. B. Suche), statt einen 403/Leerlauf zu provozieren.

/** Offene Posten (fertige, noch nicht bezahlte Aufträge). */
export function useOpenItems(enabled = true) {
  return useQuery({ queryKey: ['openItems'], queryFn: api.listOpenItems, enabled })
}

/** Festgeschriebene Rechnungen (Umsatz). */
export function useInvoices(enabled = true) {
  return useQuery({ queryKey: ['invoices'], queryFn: api.listInvoices, enabled })
}

/** Archivierte (abgeschlossene) Aufträge — nur bei Bedarf laden (globale Suche). */
export function useArchive(enabled = true) {
  return useQuery({ queryKey: ['archive'], queryFn: api.listArchive, enabled })
}

/** Auftrag anlegen (idempotent über client_uuid). Bei Erfolg die Listen auffrischen; der Aufrufer
 *  navigiert mit der zurückgegebenen id ins Detail. */
export function useCreateOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateOrderRequest) => api.createOrder(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['orders'], exact: true }),
  })
}

/**
 * Status-Wechsel mit optimistischem Update + 409-Reconcile. Sendet die gelesene `version` mit; bei 409
 * (OCC-Konflikt ODER unerlaubter Statuswechsel) wird die optimistische Änderung zurückgerollt und der
 * frische Server-Stand nachgeladen. Der Detail-Cache trägt jetzt OrderDetailResponse (Auftrag + Kinder) —
 * darum die volle Antwort spreaden statt nur {order} zu setzen, sonst verschwänden Aufgaben/Teile.
 */
export function useChangeStatus(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { status: string; version: number }) => api.patchOrder(id, { status: vars.status, version: vars.version }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['orders', id] })
      const previous = qc.getQueryData<OrderDetailResponse>(['orders', id])
      if (previous) {
        qc.setQueryData<OrderDetailResponse>(['orders', id], { ...previous, order: { ...previous.order, status: vars.status } })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['orders', id], context.previous) // Rollback
      void qc.invalidateQueries({ queryKey: ['orders', id] })
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
    },
    onSuccess: (data) => {
      // Den frischen Auftrag in den Detail-Cache mergen — Aufgaben/Teile/Positionen bleiben erhalten.
      qc.setQueryData<OrderDetailResponse>(['orders', id], (prev) => (prev ? { ...prev, order: data.order } : prev))
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
    },
  })
}

/**
 * Kind-Mutationen (Aufgaben/Teile/Positionen) liefern JEWEILS das frische OrderDetailResponse zurück — das
 * wird direkt in den Detail-Cache gesetzt (kein Re-Fetch nötig); die Liste (Aufgaben-Zähler) zieht exakt nach.
 * `alsoInvalidate` invalidiert zusätzliche Query-Keys — Teile-Mutationen spiegeln auf das Einkauf-Board
 * (order_oneoff), darum ziehen sie ['procurement'] nach.
 */
function useOrderChild<V>(id: number, run: (vars: V) => Promise<OrderDetailResponse>, alsoInvalidate?: readonly string[]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: (data) => {
      qc.setQueryData<OrderDetailResponse>(['orders', id], data)
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
      if (alsoInvalidate) void qc.invalidateQueries({ queryKey: alsoInvalidate })
    },
  })
}

export function useAddTask(id: number) {
  return useOrderChild(id, (text: string) => api.addTask(id, { text }))
}
export function useToggleTask(id: number) {
  return useOrderChild(id, (vars: { taskId: number; done: boolean }) => api.patchTask(vars.taskId, { done: vars.done }))
}
export function useDeleteTask(id: number) {
  return useOrderChild(id, (taskId: number) => api.deleteTask(taskId))
}

export function useAddPart(id: number) {
  return useOrderChild(id, (body: AddPartRequest) => api.addPart(id, body), ['procurement'])
}
export function usePatchPart(id: number) {
  return useOrderChild(id, (vars: { partId: number; body: PatchPartRequest }) => api.patchPart(vars.partId, vars.body), ['procurement'])
}
export function useDeletePart(id: number) {
  return useOrderChild(id, (partId: number) => api.deletePart(partId), ['procurement'])
}
export function useAddItem(id: number) {
  return useOrderChild(id, (body: AddItemRequest) => api.addItem(id, body))
}
export function usePatchItem(id: number) {
  return useOrderChild(id, (vars: { itemId: number; body: PatchItemRequest }) => api.patchItem(vars.itemId, vars.body))
}
export function useDeleteItem(id: number) {
  return useOrderChild(id, (itemId: number) => api.deleteItem(itemId))
}

/**
 * Allgemeiner Auftrags-Patch (Bezahlt/Archivieren/Arbeitslohn/Verknüpfen). `version` (OCC) ist Pflicht.
 * Der frische Auftrag wird in den Detail-Cache gemergt; betroffene Listen (Board/Archiv/Offene/Rechnungen)
 * werden invalidiert. Die Antwort trägt `skipped[]` — Felder, die der Server bewusst NICHT geändert hat
 * (Recht fehlt oder nach Rechnung/Freigabe GoBD-gesperrt); die UI zeigt das ehrlich an.
 */
export function usePatchOrder(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PatchOrderRequest) => api.patchOrder(id, body),
    onSuccess: (data) => {
      qc.setQueryData<OrderDetailResponse>(['orders', id], (prev) => (prev ? { ...prev, order: data.order } : prev))
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
      void qc.invalidateQueries({ queryKey: ['archive'] })
      void qc.invalidateQueries({ queryKey: ['openItems'] })
      void qc.invalidateQueries({ queryKey: ['invoices'] })
    },
    // OCC-Vertrag: bei 409 (oder jedem Fehler) NICHT blind erneut schreiben, sondern den frischen
    // Server-Stand nachladen, damit die UI gegen die aktuelle version reconcilen kann. ZUSÄTZLICH die
    // LISTE invalidieren (wie useChangeStatus): die Plantafel liest ihre version aus ['orders'] — nur den
    // Detail-Key zu invalidieren refetcht dort nichts (Query nicht gemountet) → Endlos-409 (Review-Fund 15.07.).
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['orders', id] })
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
    },
  })
}

/**
 * Kostenvoranschlag versenden. Der frische Auftrag (estimateStatus 'sent') wird in den Detail-Cache gemergt
 * und die Listen ziehen nach. Der EINMALIGE Klartext-Token bleibt in `data.token` der Mutation — die UI baut
 * daraus den Freigabe-Link; bei Reload ist er weg (die DB hält nur den Hash), genau wie im Alt-Monolithen.
 */
export function useSendEstimate(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.sendEstimate(id),
    onSuccess: (data) => {
      qc.setQueryData<OrderDetailResponse>(['orders', id], (prev) => (prev ? { ...prev, order: data.order } : prev))
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
    },
  })
}

/** Auftrag löschen. Erfolg frischt die Listen (Board/Archiv/Offene/Rechnungen); der Aufrufer navigiert
 *  zurück zur Übersicht (das Detail dieses Auftrags existiert dann nicht mehr). */
export function useDeleteOrder(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.deleteOrder(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
      void qc.invalidateQueries({ queryKey: ['archive'] })
      void qc.invalidateQueries({ queryKey: ['openItems'] })
      void qc.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

// Rechnungs-/Storno-Mutationen aktualisieren denselben Cache-Satz: den Auftrag (Spiegel invNo), die aktive
// Beleg-Ansicht (['orderInvoice', id]) und die Listen (Board/Offene/Rechnungen).
function afterInvoiceMutation(qc: ReturnType<typeof useQueryClient>, id: number, order: OrderDetailResponse['order']) {
  qc.setQueryData<OrderDetailResponse>(['orders', id], (prev) => (prev ? { ...prev, order } : prev))
  void qc.invalidateQueries({ queryKey: ['orders'], exact: true })
  void qc.invalidateQueries({ queryKey: ['orderInvoice', id] })
  void qc.invalidateQueries({ queryKey: ['openItems'] })
  void qc.invalidateQueries({ queryKey: ['invoices'] })
}

/** Rechnung festschreiben (§14-Beleg einfrieren). Der frische Auftrag (mit invNo) landet im Detail-Cache. */
export function useCreateInvoice(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.createInvoice(id),
    onSuccess: (data) => afterInvoiceMutation(qc, id, data.order),
  })
}

/** Rechnung stornieren: Stornorechnung anlegen (append-only), Auftrag wieder öffnen (invNo → null). */
export function useStornoInvoice(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.stornoInvoice(id),
    onSuccess: (data) => afterInvoiceMutation(qc, id, data.order),
  })
}

/** Aktiver, eingefrorener Beleg eines Auftrags (finale Rechnungsansicht). `enabled` nur, wenn festgeschrieben
 *  (sonst würde der Endpunkt 404en). */
export function useOrderInvoice(id: number, enabled: boolean) {
  return useQuery({ queryKey: ['orderInvoice', id], queryFn: () => api.getOrderInvoice(id), enabled: enabled && Number.isFinite(id) })
}

/** Einzelner Beleg (Rechnung ODER Stornorechnung) aus dem Ledger — für die Beleg-Ansicht aus der Liste. */
export function useInvoiceDoc(invoiceId: number) {
  return useQuery({ queryKey: ['invoiceDoc', invoiceId], queryFn: () => api.getInvoice(invoiceId), enabled: Number.isFinite(invoiceId) })
}
